<template>
  <!-- Centralizator Intrări – Ieșiri – Producție : réceptions, production et
       ventes par mois et catégorie de produit, bornées par une période.
       Mêmes chiffres que le KPI 21 « Export Carmen » (service partagé côté API). -->
  <div id="page-reports-centralizator">
    <vx-card
      title="Centralizator intrări – ieșiri – producție"
      subtitle="Receptions, production and sales per month and product category">

      <div class="centralizator-toolbar">
        <div class="centralizator-toolbar__dates">
          <vs-input type="date" label="From" v-model="from" class="mr-4" />
          <vs-input type="date" label="To" v-model="to" />
        </div>
        <div class="centralizator-toolbar__actions">
          <vs-button size="small" type="border" icon-pack="feather" icon="icon-refresh-cw"
            :disabled="loading" @click="load">Refresh</vs-button>
          <vs-button class="ml-2" size="small" color="success" icon-pack="feather" icon="icon-download"
            :disabled="loading || !rows.length" @click="downloadXlsx">Download Excel</vs-button>
        </div>
      </div>

      <div v-if="loaded && !error" class="centralizator-summary mt-4">
        <vs-chip>{{ period.from }} → {{ period.to }}</vs-chip>
        <vs-chip color="primary">{{ rows.length }} {{ rows.length === 1 ? 'row' : 'rows' }}</vs-chip>
        <vs-chip v-if="totals.rec_volume !== null" color="success">Receptions {{ fmt(totals.rec_volume, 3) }} m³</vs-chip>
        <vs-chip v-if="totals.prod_volume_out !== null" color="warning">Produced {{ fmt(totals.prod_volume_out, 3) }} m³</vs-chip>
        <vs-chip v-if="totals.sale_volume !== null" color="danger">Sales {{ fmt(totals.sale_volume, 3) }} m³</vs-chip>
      </div>

      <vs-alert v-if="error" color="danger" icon-pack="feather" icon="icon-alert-circle" :active="true" class="mt-4">
        {{ error }}
      </vs-alert>

      <div v-else-if="loaded && !rows.length" class="mt-6 mb-4">
        <h4 style="text-align:center">No movements in this period</h4>
        <p class="text-center text-grey text-sm mt-2">No reception, production or sale dated between {{ period.from }} and {{ period.to }}.</p>
      </div>

      <div v-for="group in months" :key="group.key" class="mt-6 centralizator-month">
        <h5 class="mb-2">{{ group.title }}</h5>
        <vs-table :data="group.rows" class="centralizator-table">
          <template slot="thead">
            <vs-th>Category</vs-th>
            <vs-th v-for="c in columns" :key="c.key" class="centralizator-th">{{ c.label }}</vs-th>
          </template>
          <template slot-scope="{ data }">
            <vs-tr v-for="(tr, i) in data" :key="i" :class="{ 'centralizator-total': tr._total }">
              <vs-td>{{ tr.product_category }}</vs-td>
              <vs-td v-for="c in columns" :key="c.key" class="centralizator-num">{{ fmt(tr[c.key], c.decimals) }}</vs-td>
            </vs-tr>
          </template>
        </vs-table>
      </div>
    </vx-card>
  </div>
</template>

<script>
import moment from 'moment'
import moduleReports from '@/store/reports/moduleReports.js'
import { canSeeReports, landingPathFor } from '@/helpers/roleAccess.js'

const MONTHS_EN = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
]

// Colonnes numériques, dans l'ordre de l'export Excel (en-têtes roumains côté API).
const COLUMNS = [
  { key: 'rec_volume',      label: 'Receptions vol (m³)', decimals: 3 },
  { key: 'rec_qty',         label: 'Receptions qty',      decimals: 2 },
  { key: 'rec_value_eur',   label: 'Receptions EUR',      decimals: 2 },
  { key: 'rec_value_ron',   label: 'Receptions RON',      decimals: 2 },
  { key: 'prod_volume_out', label: 'Produced vol (m³)',   decimals: 3 },
  { key: 'prod_qty_out',    label: 'Produced qty',        decimals: 2 },
  { key: 'prod_volume_in',  label: 'Consumed vol (m³)',   decimals: 3 },
  { key: 'prod_qty_in',     label: 'Consumed qty',        decimals: 2 },
  { key: 'sale_volume',     label: 'Sales vol (m³)',      decimals: 3 },
  { key: 'sale_qty',        label: 'Sales qty',           decimals: 2 },
  { key: 'sale_value_eur',  label: 'Sales EUR',           decimals: 2 },
  { key: 'sale_value_ron',  label: 'Sales RON',           decimals: 2 }
]

function emptyTotals () {
  const t = {}
  COLUMNS.forEach(c => { t[c.key] = null })
  return t
}

export default {
  name: 'ReportsCentralizator',
  data () {
    return {
      from: moment().startOf('month').format('YYYY-MM-DD'),
      to: moment().endOf('month').format('YYYY-MM-DD'),
      period: { from: null, to: null },
      rows: [],
      totals: emptyTotals(),
      columns: COLUMNS,
      loading: false,
      loaded: false,
      error: null
    }
  },
  computed: {
    // Une table par mois, une ligne « Total » en bas de chacune.
    months () {
      const groups = []
      const byKey = {}
      this.rows.forEach(r => {
        const key = `${r.year}-${String(r.month).padStart(2, '0')}`
        if (!byKey[key]) {
          byKey[key] = { key, title: `${MONTHS_EN[r.month - 1] || r.month_name} ${r.year}`, rows: [] }
          groups.push(byKey[key])
        }
        byKey[key].rows.push(r)
      })
      groups.forEach(g => {
        const total = { product_category: 'Total', _total: true }
        COLUMNS.forEach(c => {
          let sum = null
          g.rows.forEach(r => {
            if (r[c.key] === null || r[c.key] === undefined) return
            sum = (sum === null ? 0 : sum) + Number(r[c.key])
          })
          total[c.key] = sum
        })
        g.rows = g.rows.concat([total])
      })
      return groups
    }
  },
  methods: {
    fmt (value, decimals) {
      if (value === null || value === undefined || value === '') return '–'
      return Number(value).toLocaleString('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      })
    },
    load () {
      if (!this.from || !this.to) {
        this.error = 'Please select both dates.'
        return
      }
      if (this.from > this.to) {
        this.error = '"From" is after "To".'
        return
      }
      this.loading = true
      this.error = null
      this.$vs.loading()
      this.$store
        .dispatch('reports/fetchCentralizator', { from: this.from, to: this.to })
        .then(res => {
          const data = res.data || {}
          this.period = { from: data.from, to: data.to }
          this.rows = data.rows || []
          this.totals = Object.assign(emptyTotals(), data.totals || {})
          this.loaded = true
        })
        .catch(err => {
          console.error(err)
          const status = err && err.response && err.response.status
          const message = err && err.response && err.response.data && err.response.data.message
          if (status === 403) {
            this.denied()
            return
          }
          this.error = message || 'Could not load the report.'
        })
        .finally(() => {
          this.$vs.loading.close()
          this.loading = false
        })
    },
    downloadXlsx () {
      this.loading = true
      this.$vs.loading()
      this.$store
        .dispatch('reports/downloadCentralizatorXlsx', { from: this.from, to: this.to })
        .catch(err => {
          console.error(err)
          this.$vs.notify({
            color: 'danger',
            title: 'Error',
            text: 'Could not generate the Excel file.',
            position: 'bottom-right'
          })
        })
        .finally(() => {
          this.$vs.loading.close()
          this.loading = false
        })
    },
    denied () {
      this.$vs.notify({
        title: 'Denied',
        text: 'Redirection',
        iconPack: 'feather',
        icon: 'icon-alert-circle',
        color: 'danger',
        position: 'bottom-right',
        time: 8000
      })
      this.$router.push({ path: landingPathFor() }).catch(() => {})
    }
  },
  created () {
    if (!canSeeReports()) {
      this.denied()
      return
    }
    if (!moduleReports.isRegistered) {
      this.$store.registerModule('reports', moduleReports)
      moduleReports.isRegistered = true
    }
    this.load()
  }
}
</script>

<style lang="scss">
#page-reports-centralizator {
  .centralizator-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    justify-content: space-between;
    gap: 1rem;
  }
  .centralizator-toolbar__dates {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
  }
  // Les vs-chip de Vuesax flottent (float: left) : un conteneur flex les tient
  // sur leur ligne et les mois repartent dessous.
  .centralizator-summary {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
  }
  .centralizator-summary .con-vs-chip {
    float: none;
    margin-right: .5rem;
    margin-bottom: .25rem;
  }
  .centralizator-month {
    clear: both;
  }
  .centralizator-table {
    overflow-x: auto;
  }
  .centralizator-num {
    text-align: right;
    white-space: nowrap;
  }
  .centralizator-th {
    white-space: nowrap;
  }
  .centralizator-total td {
    font-weight: 600;
    border-top: 2px solid rgba(0, 0, 0, .15);
  }
}
</style>
